const array = (value) => {
  if (Array.isArray(value)) return value;
  if (typeof value !== "string") return [];
  try { const parsed = JSON.parse(value); return Array.isArray(parsed) ? parsed : []; } catch { return []; }
};

const number = (value, fallback = 0) => Number.isFinite(Number(value)) ? Number(value) : fallback;

export const normalizeTeamName = (value) => String(value ?? "")
  .trim()
  .replace(/\s+/g, " ")
  .toLocaleLowerCase("tr-TR");

const useful = (value) => value !== undefined && value !== null &&
  (typeof value !== "string" || value.trim() !== "");

export const removeEmptyMetaValues = (value = {}) => Object.fromEntries(
  Object.entries(value).filter(([, item]) => useful(item))
);

export const ensureAbsoluteUrl = (value, apiBase = "") => {
  if (typeof value !== "string") return "";
  const url = value.trim();
  if (!url || ["null", "undefined"].includes(url.toLocaleLowerCase("en-US"))) return "";
  if (/^https?:\/\//i.test(url)) return url;
  if (url.startsWith("//")) return `https:${url}`;
  if (!apiBase) return url;
  return `${String(apiBase).replace(/\/+$/, "")}/${url.replace(/^\/+/, "")}`;
};

const teamFields = (value) => {
  if (!value || typeof value !== "object") return {};
  return removeEmptyMetaValues({
    id: value.id ?? value.teamId ?? value.team_id ?? value.takim_id,
    name: value.name ?? value.teamName ?? value.team_name ?? value.takim_adi,
    logo: value.logo ?? value.teamLogo ?? value.team_logo ?? value.logo_url ?? value.image,
  });
};

/** Resolve team metadata across canonical, transitional and legacy payloads. */
export const normalizeTeamMeta = (payload = {}, side, previous = {}, teamsByName) => {
  const raw = payload?.snapshot ?? payload?.data?.snapshot ?? payload?.data ?? payload;
  const match = raw?.match ?? raw?.mac ?? raw?.matchData ?? raw;
  const isHome = side === "home";
  const candidates = [
    raw?.teams?.[side],
    raw?.[isHome ? "homeTeam" : "awayTeam"],
    raw?.[isHome ? "firstTeam" : "secondTeam"],
    match?.teams?.[side],
    match?.[isHome ? "homeTeam" : "awayTeam"],
    match?.[isHome ? "firstTeam" : "secondTeam"],
  ];
  let resolved = {};
  candidates.slice().reverse().forEach((candidate) => {
    resolved = { ...resolved, ...teamFields(candidate) };
  });
  const legacy = removeEmptyMetaValues({
    id: match?.[isHome ? "home_team_id" : "away_team_id"] ??
      match?.[isHome ? "first_team_id" : "second_team_id"] ??
      raw?.[isHome ? "home_team_id" : "away_team_id"],
    name: match?.[isHome ? "first_team_name" : "second_team_name"] ??
      match?.[isHome ? "home_team_name" : "away_team_name"] ??
      raw?.[isHome ? "first_team_name" : "second_team_name"],
    logo: match?.[isHome ? "first_team_logo" : "second_team_logo"] ??
      match?.[isHome ? "home_team_logo" : "away_team_logo"],
  });
  resolved = { ...legacy, ...resolved };
  const name = resolved.name ?? previous.name;
  const fallback = name && teamsByName?.get(normalizeTeamName(name));
  return {
    ...previous,
    ...teamFields(fallback),
    ...removeEmptyMetaValues(resolved),
  };
};

export const payloadMatchId = (payload) =>
  payload?.matchId ?? payload?.match_id ?? payload?.mac_id ?? payload?.match?.id ?? payload?.mac?.id;

export const payloadRevision = (payload) => {
  const value = payload?.revision ?? payload?.seq ?? payload?.version;
  return value == null ? null : number(value, null);
};

/**
 * Sunucu ile tarayıcı saatleri arasındaki farkı ms cinsinden döndürür.
 *
 * `baseAt` SUNUCU saat koordinatındadır (DB'deki `base_at`). Overlay'ler süreyi
 * `Date.now() - baseAt` ile hesapladığı için, yayın makinesinin saati sunucudan
 * sapmışsa süre tam o sapma kadar kayar: saat 13 sn geride ise süre de 13 sn
 * geriden başlar. Sunucu her snapshot/delta ile `serverNow` gönderiyor; iki
 * saati burada aynı koordinata getiriyoruz.
 *
 * Ölçüme tek yönlü ağ gecikmesi de karışır (tipik olarak < 200 ms). Bu, saniye
 * çözünürlüklü bir maç saati için önemsizdir; ayrıca aşağıdaki monotonluk
 * koruması, sapma ölçümündeki oynamanın saati geriye almasını engeller.
 */
const serverClockOffset = (timer, value, previous, now) => {
  const rawServerNow =
    timer?.serverNow ??
    timer?.server_now ??
    timer?.serverTime ??
    timer?.server_time ??
    // Socket delta'larında `serverNow` mesajın kökünde, `timer` alt nesnesinin
    // dışında geliyor (bkz. matchRealtimeService.publishToMatch).
    value?.serverNow ??
    value?.server_now ??
    value?.serverTime ??
    value?.server_time;

  const parsed = rawServerNow == null
    ? NaN
    : typeof rawServerNow === "number"
    ? rawServerNow
    : Date.parse(rawServerNow);

  if (!Number.isFinite(parsed)) return number(previous?.clockOffsetMs, 0);
  return parsed - now;
};

const readTimerPeriod = (timer, root) => {
  const raw = timer?.period ?? root?.period ?? root?.live_period ?? root?.livePeriod;
  const n = Number(raw);
  return raw == null || raw === "" || !Number.isFinite(n) || n <= 0 ? null : Math.trunc(n);
};

export const normalizeTimer = (value = {}, previous = {}, now = Date.now()) => {
  const timer = value?.timer ?? value?.liveTimer ?? value;
  const timerKeys = [
    "running", "isRunning", "baseMs", "base_ms", "baseAt", "base_at",
    "clockMs", "updatedAt", "serverNow", "server_now",
    "live_timer_state", "live_timer_running", "live_timer_reference_ms",
    "live_timer_updated_at",
  ];
  const hasTimerData = timer && typeof timer === "object" &&
    timerKeys.some(key => Object.prototype.hasOwnProperty.call(timer, key));

  // Olay/skor snapshot'ları çoğu zaman timer taşımaz. Önceki uygulama bu
  // durumda baseMs'i korurken baseAt'i Date.now() yapıyordu. Böylece her olay
  // çalışan saatin başlangıç noktasını ileri taşıyor ve ekrandaki süre geriye
  // sıçrıyordu. Timer verisi yoksa anchor'a hiç dokunma.
  if (!hasTimerData) return { ...previous };

  const rawRunning =
    timer.running ??
    timer.isRunning ??
    timer.live_timer_running ??
    (timer.live_timer_state != null
      ? String(timer.live_timer_state).toLowerCase() === "running"
      : undefined);
  const running = rawRunning === undefined
    ? Boolean(previous.running)
    : rawRunning === true || rawRunning === 1 || rawRunning === "1" || rawRunning === "true";
  const rawBaseMs =
    timer.baseMs ??
    timer.base_ms ??
    timer.clockMs ??
    timer.live_timer_reference_ms;
  const baseMs = number(rawBaseMs, previous.baseMs || 0);
  const rawBaseAt =
    timer.baseAt ??
    timer.base_at ??
    timer.updatedAt ??
    timer.live_timer_updated_at ??
    timer.serverNow ??
    timer.server_now;
  const parsedAt = rawBaseAt == null
    ? NaN
    : typeof rawBaseAt === "number"
    ? rawBaseAt
    : Date.parse(rawBaseAt);
  const anchorChanged =
    rawBaseMs !== undefined ||
    rawRunning !== undefined;
  const clockOffsetMs = serverClockOffset(timer, value, previous, now);
  // `baseAt` HER ZAMAN sunucu koordinatında tutulur; yerel saate düşmek
  // gerektiğinde sapma eklenerek aynı koordinata taşınır.
  const localAnchor = now + clockOffsetMs;
  const next = {
    ...previous,
    running,
    baseMs: Math.max(0, baseMs),
    baseAt: Number.isFinite(parsedAt)
      ? parsedAt
      : anchorChanged
      ? localAnchor
      : previous.baseAt ?? localAnchor,
    clockOffsetMs,
    serverNow: timer.serverNow ?? timer.server_now ?? null,
    revision: payloadRevision(timer) ?? previous.revision ?? 0,
    // Devre (1 = ilk yarı, 2 = ikinci yarı, 3 = uzatma). Sayaç satırında
    // tutulur; anlık görüntüde kökte (period / live_period) gelir.
    period: readTimerPeriod(timer, value) ?? previous.period ?? null,
  };

  // Monotonluk koruması: çalışan bir saat, ağ gecikmesi, backend'in
  // `live_time` (mm:ss) alanını saniyeye yuvarlaması veya bayat bir anchor
  // yüzünden GERİYE gitmemelidir. Panel `reconcileRunningTimer` ile bunu
  // zaten yapıyor; overlay'ler ise `live.timer`'ı doğrudan okuduğundan aynı
  // garantiyi burada, tüm tüketicilerin paylaştığı tek noktada sağlıyoruz.
  // Duraklatma/reset/geri sarma gibi bilinçli işlemler `running: false` ile
  // geldiğinden (manuel süre girişi de timer'ı duraklatır) etkilenmez.
  if (previous.running && running) {
    const previousMs = calculateTimerMs(previous, now);
    const nextMs = calculateTimerMs(next, now);
    if (nextMs < previousMs) {
      return { ...next, baseMs: previousMs, baseAt: localAnchor };
    }
  }

  return next;
};

export const eventIdentity = (event) => String(
  event?.id ?? event?.serverId ?? event?.eventId ?? event?.clientMutationId ??
  event?.client_event_id ?? event?.clientEventId ?? ""
);

const objectMeta = (value) => value && typeof value === "object" ? value : {};

/** Oyuncu değişikliği (SUBSTITUTION) taraflarını tek biçime indirger. */
const normalizeSubstitutionSide = (meta, fallbackId) => {
  const id = meta.id ?? meta.playerId ?? meta.player_id ?? meta.oyuncu_id ?? fallbackId ?? null;
  const name = meta.name ?? meta.player_name ?? meta.playerName ?? "";
  const image = meta.image ?? meta.photo ?? meta.player_image ?? meta.player_img ?? "";
  if (id == null && !name) return null;
  return { ...meta, id, name, image };
};

/** Normalize notification metadata without removing legacy fields used by overlays. */
export const normalizeLiveEvent = (value = {}) => {
  const raw = value?.event ?? value?.data ?? value;
  const player = objectMeta(raw.player ?? raw.oyuncu);
  const team = objectMeta(raw.team ?? raw.takim);
  const assistPlayer = objectMeta(raw.assistPlayer ?? raw.assist_player);
  const substitutePlayer = objectMeta(raw.substitutePlayer ?? raw.playerIn ?? raw.giren_oyuncu);
  // Değişiklik olayı: sunucu socket yayını outgoingPlayer/incomingPlayer nesneleri,
  // snapshot/DB satırları ise yalnızca oyuncu_cikan_id/oyuncu_giren_id taşır.
  const outgoingMeta = objectMeta(raw.outgoingPlayer ?? raw.outgoing_player ?? raw.cikan_oyuncu);
  const incomingMeta = objectMeta(raw.incomingPlayer ?? raw.incoming_player ?? raw.substitutePlayer ?? raw.playerIn ?? raw.giren_oyuncu);
  const outgoingPlayerId = raw.oyuncu_cikan_id ?? raw.outgoingPlayerId ?? raw.outgoing_player_id ?? outgoingMeta.id ?? null;
  const incomingPlayerId = raw.oyuncu_giren_id ?? raw.incomingPlayerId ?? raw.incoming_player_id ?? incomingMeta.id ?? null;
  const eventCode = String(raw.eventCode ?? raw.event_code ?? raw.code ?? raw.olay_kodu ?? raw.eventType ?? raw.event_type ?? raw.type ?? raw.olay_tipi ?? "").toUpperCase();
  return {
    ...raw,
    id: raw.id ?? raw.eventId ?? raw.event_id ?? raw.clientEventId ?? raw.client_event_id,
    eventCode,
    olay_kodu: raw.olay_kodu ?? eventCode,
    minute: raw.minute ?? raw.dakika ?? 0,
    dakika: raw.dakika ?? raw.minute ?? 0,
    playerId: raw.playerId ?? raw.player_id ?? raw.oyuncu_id ?? player.id,
    oyuncu_id: raw.oyuncu_id ?? raw.playerId ?? raw.player_id ?? player.id,
    playerName: player.name ?? player.player_name ?? raw.playerName ?? raw.player_name ?? raw.oyuncu_adi ?? (typeof raw.oyuncu === "string" ? raw.oyuncu : ""),
    playerImage: player.image ?? player.photo ?? player.player_image ?? player.player_img ?? raw.playerImage ?? raw.player_image ?? raw.player_img ?? "",
    player: { ...player, name: player.name ?? player.player_name ?? raw.playerName ?? raw.player_name ?? raw.oyuncu_adi ?? "", image: player.image ?? player.photo ?? player.player_image ?? player.player_img ?? raw.playerImage ?? raw.player_img ?? "" },
    teamId: raw.teamId ?? raw.team_id ?? raw.takim_id ?? team.id,
    takim_id: raw.takim_id ?? raw.teamId ?? raw.team_id ?? team.id,
    side: raw.side ?? raw.taraf,
    team: { ...team, name: team.name ?? team.team_name ?? raw.teamName ?? raw.team_name ?? "", logo: team.logo ?? team.team_logo ?? raw.teamLogo ?? raw.team_logo ?? "" },
    assistPlayer,
    substitutePlayer,
    oyuncu_cikan_id: outgoingPlayerId,
    oyuncu_giren_id: incomingPlayerId,
    outgoingPlayerId,
    incomingPlayerId,
    outgoingPlayer: normalizeSubstitutionSide(outgoingMeta, outgoingPlayerId),
    incomingPlayer: normalizeSubstitutionSide(incomingMeta, incomingPlayerId),
    score: raw.score ?? { home: raw.homeScore ?? raw.first_team_score, away: raw.awayScore ?? raw.second_team_score },
  };
};

export const normalizeSnapshot = (payload = {}, previous = {}, now = Date.now()) => {
  const raw = payload?.snapshot ?? payload?.data?.snapshot ?? payload?.data ?? payload;
  const match = raw.match ?? raw.mac ?? raw.matchData ?? raw;
  const events = array(raw.events ?? raw.olaylar ?? match.live_events ?? previous.events).map(normalizeLiveEvent);
  const lineupRaw = raw.lineups ?? raw.lineup ?? raw.kadro ?? {};
  const lineups = {
    home: array(lineupRaw.home ?? match.live_home_lineup ?? previous.lineups?.home),
    away: array(lineupRaw.away ?? match.live_away_lineup ?? previous.lineups?.away),
  };
  return {
    match: { ...(previous.match || {}), ...match },
    homeTeam: normalizeTeamMeta(raw, "home", previous.homeTeam),
    awayTeam: normalizeTeamMeta(raw, "away", previous.awayTeam),
    score: {
      home: number(raw.score?.home ?? match.first_team_score ?? match.homeScore, previous.score?.home || 0),
      away: number(raw.score?.away ?? match.second_team_score ?? match.awayScore, previous.score?.away || 0),
    },
    // Kök nesne verilir: devre (period) anlık görüntüde sayaçla değil kökte gelir.
    timer: normalizeTimer(raw.timer ? raw : match, previous.timer, now),
    events,
    lineups,
    lineupVersion: number(raw.lineupVersion ?? raw.lineup_version, previous.lineupVersion || 0),
    revision: payloadRevision(raw) ?? payloadRevision(match) ?? previous.revision ?? 0,
  };
};

/**
 * Gösterilecek süreyi ms cinsinden hesaplar.
 *
 * `now` yerel (tarayıcı) saatidir, `baseAt` ise sunucu saatidir. İkisini
 * çıkarmadan önce yerel saat `clockOffsetMs` ile sunucu koordinatına taşınır;
 * aksi halde yayın makinesinin saat sapması doğrudan süreye yansır.
 */
export const calculateTimerMs = (timer, now = Date.now()) => {
  if (!timer) return 0;
  const serverNow = now + number(timer.clockOffsetMs, 0);
  return Math.max(0, number(timer.baseMs) + (timer.running ? Math.max(0, serverNow - number(timer.baseAt, serverNow)) : 0));
};
